// ─────────────────────────────────────────────────────────────────────────────
// Shared constants for scoring components.
// Values sourced directly from the approved prototype: hauserEQ_23020_lita_v8_nhd.jsx
// ─────────────────────────────────────────────────────────────────────────────

// Surface/ink keys follow the Day/Night theme tokens; status hues stay literal.
export const COLORS: Record<string, string> = {
  bg: "#1a1a1a",
  card: 'var(--tx-glass-panel)',
  cardAlt: 'rgba(var(--tx-ink-rgb), 0.05)',
  border: 'var(--tx-line)',
  text: 'var(--tx-text)',
  textMuted: 'var(--tx-text)',
  textDim: 'var(--tx-muted)',
  accent1: "#5AE9FF",
  accent2: "#bbb7af",
  critical: "#DB5282",
  urgent: "#FF9A4D",
  moderate: "#FFF981",
  low: "#5AE9FF",
  monitor: "#9A938A",
  positive: "#5AE9FF",
  negative: "#FF9A4D",
  outerBg: "#bbb7af",
  nhdAccent: "#bbb7af",
  nhdBg: "rgba(187,183,175,0.08)",
  // Existing HOA document-classification purple, shared here for HOA-specific
  // scoring semantics such as an active system concern.
  hoaPurple: "#8E7CFF",
  fireRed: "#c0392b",
  fireRedBg: "#3a1111",
  fireRedBorder: "#8b2020",
  // Same lime green already used for success/confirmed indicators in
  // PropertiesTab.tsx and PropertiesPageNew.tsx (`C.lime`) — one shared
  // source of truth rather than duplicating the hex value again here.
  lime: "#c0ff02",
};

// Top-level score tooltips (prototype lines 379–383)
export const SCORE_TOOLTIPS: Record<string, string> = {
  ConditionScore:
    "The physical condition of the home based on a professional inspection. Covers structure, systems, safety, and functionality.",
  ContextScore:
    "Everything outside the four walls — location quality, natural hazards, and neighborhood services.",
  HauserEQ:
    "Your complete home intelligence score combining what's inside the house (Condition) with what's around it (Context).",
};

// Subscore tooltips
export const SUBSCORE_TOOLTIPS: Record<string, string> = {
  HomeHealth: "The soundness of the home's structure, systems, and envelope.",
  RepairBurden: 'The expected cost of repairs flagged during inspection, after closing.',
  LocationQuality:
    'The strength of the surrounding area — schools, amenities, and neighborhood character.',
  RiskExposure:
    "The property's exposure to natural hazards, crime, and other safety risks.",
};

// Condition radar pillar tooltips (prototype COND_RADAR_TIPS)
export const COND_RADAR_TOOLTIPS: Record<string, string> = {
  'STRUCTURAL': 'Foundation, framing, and load-bearing elements. The bones of the house — expensive to fix if compromised.',
  'EXTERIOR': 'Roof, siding, pool, drainage, fencing, garage, and landscaping. Everything you see from outside.',
  'INTERIOR': 'Walls, floors, doors, kitchen, bathrooms, and indoor environmental concerns like mold or air quality.',
  'HVAC SYSTEMS': 'Heating, air conditioning, fireplaces, ductwork, and ventilation. Comfort and safety systems.',
  'HVAC & SYSTEMS': 'Heating, air conditioning, fireplaces, ductwork, and ventilation. Comfort and safety systems.',
  'HVAC': 'Heating, air conditioning, fireplaces, ductwork, and ventilation. Comfort and safety systems.',
  'PLUMBING': 'Water supply, drains, sewer lines, water heater, gas piping, and fixtures. The hidden infrastructure.',
  'ELECTRICAL': 'Panel, wiring, outlets, GFCI protection, smoke/CO detectors, and pool electrical safety.',
};

// Context radar pillar tooltips (prototype CTX_RADAR_TIPS)
export const CTX_RADAR_TOOLTIPS: Record<string, string> = {
  'NATURAL_HAZARDS': 'Natural hazard exposure — flood, fire, seismic, storms, winter weather, soil, contamination, and recent disasters.',
  'HEALTH_QUALITY': 'Air quality, water quality, noise exposure, radon, and mold conditions that may affect everyday health and comfort.',
  'SAFETY': 'Crime rates, violent and property crime trends in the immediate area.',
  'LOCATION': 'Walkability, transit access, and access to nearby fire, law-enforcement, and medical response services.',
  'AMENITIES': 'Access to schools and essential neighborhood services.',
  'SITE': 'Parcel-specific characteristics — lot dimensions, elevation context, utility service, and local building standards.',
};

// Subscores intentionally hidden from the launch UI. They're still
// computed by hauser-eq and stored on the property record — we just don't
// surface them in cards, radars, drivers, or the AI context. The scoring
// config in LogicTab grays the rows out so admins can see them but can't
// edit. Drop a name from this set when we ship the replacement metrics.
export const HIDDEN_SUBSCORES = new Set<string>();

export const isHiddenSubscore = (name: string | null | undefined): boolean =>
  name != null && HIDDEN_SUBSCORES.has(name);

// Urgency level tooltips (prototype lines 471–477)
export const URGENCY_TOOLTIPS: Record<string, string> = {
  Critical: "Immediate safety hazard or code violation. Recommended to address before moving in.",
  Urgent:
    "Significant issue that risks further damage if delayed. Worth addressing within the first 30 days.",
  Moderate:
    "Worth repairing within the first year. Not a safety concern but affects function or longevity.",
  Low: "Minor cosmetic or maintenance item. Address at your convenience.",
  Monitor: "Not a defect today, but worth watching over time. May need attention in 2-5 years.",
};
